// Schemas for routes-recording.ts (browser-safe zod). Call recording + consent notice on Telnyx calls (plan B4).
//
// Honesty rules encoded here:
//  • recording is OFF by default for every call type: nothing is recorded until an admin turns it on;
//  • a call is only recorded after Telnyx confirmed the consent notice finished playing to the prospect; a notice that
//    failed (or could not be confirmed) means the call is NOT recorded, and the call detail says so;
//  • the stored file lives in LIA's private storage and is only streamed through an authenticated, workspace-checked
//    API route — never a Telnyx URL in the browser;
//  • everything Telnyx-facing is «sin verificar» until a real account records a call.
import { z } from 'zod';

const Iso = z.string();

export const DEFAULT_CONSENT_NOTICE = 'Esta llamada puede ser grabada y transcrita para calidad y seguimiento.';
export const CONSENT_NOTICE_MAX = 400;

export const CallRecordingSettingsSchema = z.object({
  /** AI calls on Telnyx lines (plan B2). */
  aiCalls: z.boolean(),
  /** Advisor calls through the softphone (plan B3). */
  advisorCalls: z.boolean(),
  /** Spanish notice played to the prospect before recording starts. */
  noticeText: z.string(),
  updatedAt: Iso.nullable(),
});
export type CallRecordingSettings = z.infer<typeof CallRecordingSettingsSchema>;

export const UpdateCallRecordingSettingsSchema = z.object({
  aiCalls: z.boolean(),
  advisorCalls: z.boolean(),
  noticeText: z.string().trim().min(10, 'Escribe un aviso de al menos 10 caracteres').max(CONSENT_NOTICE_MAX, `Máximo ${CONSENT_NOTICE_MAX} caracteres`),
});
export type UpdateCallRecordingSettings = z.infer<typeof UpdateCallRecordingSettingsSchema>;

/**
 * What happened with the recording of one call, as shown in the call detail:
 *  - provider:   Aircall call — the recording (if any) lives in Aircall and a temporary link is generated on demand;
 *  - none:       no recording decision exists for this call (not a Telnyx call, or before plan B4);
 *  - disabled:   recording was off for this call type when the call happened;
 *  - pending:    waiting for the consent notice to finish (or for Telnyx to start/save the recording);
 *  - no_consent: the notice failed or could not be confirmed (or the call ended first) → not recorded;
 *  - processing: Telnyx saved the recording; LIA is copying it to its private storage;
 *  - available:  stored in LIA's private storage;
 *  - failed:     Telnyx refused to record, reported an error, or the copy failed;
 *  - deleted:    removed by the retention policy or a contact erasure request.
 */
export const CALL_RECORDING_STATES = ['provider', 'none', 'disabled', 'pending', 'no_consent', 'processing', 'available', 'failed', 'deleted'] as const;
export const CallRecordingStateSchema = z.enum(CALL_RECORDING_STATES);
export type CallRecordingState = z.infer<typeof CallRecordingStateSchema>;

export const CALL_RECORDING_STATE_LABELS: Record<CallRecordingState, string> = {
  provider: 'En el proveedor',
  none: 'Sin grabación',
  disabled: 'Grabación desactivada',
  pending: 'En espera del aviso',
  no_consent: 'Sin consentimiento registrado',
  processing: 'Guardando grabación',
  available: 'Grabación disponible',
  failed: 'Grabación fallida',
  deleted: 'Grabación eliminada',
};

export const CONSENT_STATUSES = ['not_required', 'pending', 'played', 'failed'] as const;
export const ConsentStatusSchema = z.enum(CONSENT_STATUSES);
export type ConsentStatus = z.infer<typeof ConsentStatusSchema>;

export const CallConsentSchema = z.object({
  status: ConsentStatusSchema,
  noticeText: z.string().nullable(),
  /** When Telnyx reported the notice finished playing (consent evidence). */
  playedAt: Iso.nullable(),
  detail: z.string().nullable(),
});
export type CallConsent = z.infer<typeof CallConsentSchema>;
